/** The one country code every box on this site collects a number under. */
export const KAZAKHSTAN_DIAL_CODE = '+7';

/** Digits a Kazakhstan number has once the country code is taken off the front. */
export const SUBSCRIBER_LENGTH = 10;

/**
 * Characters a formatted number occupies — `707 662 81 86`. The box is capped at it so a full
 * number cannot be typed past: an eleventh digit is what makes a trunk prefix and a subscriber
 * digit indistinguishable, and refusing it at the keyboard is cheaper than guessing after the fact.
 */
export const FORMATTED_LENGTH = 13;

/**
 * A Kazakhstan number, held as the ten digits that follow `+7`.
 *
 * **The country code is not part of the value, because it is not part of the question.** The field
 * shows `+7` welded to its left edge and the customer types what comes after it, so there is no
 * state in which the box holds a number belonging to somewhere else — which is what makes the check
 * below a rule about ten digits rather than a guess at an international format.
 *
 * **A mirror of the server's `PhoneNumber`, and not the authority.** The server normalises the value
 * again and its answer is the one that is stored; this exists so a mistyped number is corrected in
 * the box it was typed in instead of coming back as a rejected form.
 *
 * The narrowing is deliberate and it is the live site's own: the static form this replaces demanded
 * ten digits beginning 6 or 7 as well. The server still accepts a foreign number — `PhoneNumber`
 * declines to guess at one rather than refusing it — so a caller from abroad is recorded by staff
 * through the admin application, not turned away at the public form.
 */
export function toSubscriberDigits(input: string): string {
    const trimmed = input.trim();
    const digits = trimmed.replace(/\D/g, '');

    // A pasted number carries its country code or its trunk prefix — `+7 707…`, `8 707…` — and all
    // of them spell the same subscriber. A written-out `+7` is a country code and can be nothing
    // else; without the `+`, the leading digit is only a prefix when there is one digit too many,
    // because at ten the leading `7` is the first digit of `707`.
    const subscriber = trimmed.startsWith('+7')
        ? digits.slice(1)
        : digits.length === SUBSCRIBER_LENGTH + 1 && (digits.startsWith('7') || digits.startsWith('8'))
          ? digits.slice(1)
          : digits;

    return subscriber.slice(0, SUBSCRIBER_LENGTH);
}

/**
 * The digits grouped the way a number is read aloud here — `707 662 81 86`. Formatting as the
 * customer types is what makes a wrong digit visible before the field is left.
 */
export function formatSubscriberDigits(digits: string): string {
    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 8), digits.slice(8, 10)]
        .filter((group) => group !== '')
        .join(' ');
}

/**
 * Whether the ten digits are a number that could exist. Every Kazakhstan area and mobile code
 * begins 6 or 7, so a number starting anywhere else is a mistyped one rather than an unusual one.
 */
export function isCompleteSubscriber(digits: string): boolean {
    return /^[67]\d{9}$/.test(digits);
}

/** The value as it is sent: `+7XXXXXXXXXX`, the one shape the column holds. */
export function toE164(digits: string): string {
    return `${KAZAKHSTAN_DIAL_CODE}${digits}`;
}
